<template>
  <v-dialog
    :model-value="modelValue"
    max-width="1140"
    scrim="#000"
    :retain-focus="false"
    :content-props="{ style: contentStyle }"
    style="--v-overlay-opacity: 0.5"
    @update:modelValue="$emit('update:modelValue', $event)"
  >
    <v-card v-if="line" class="escr-line-modal">
      <div class="escr-line-modal-header" @mousedown="startDrag">
        <h2>Line #{{ line.id }}</h2>

        <v-btn
          icon
          variant="text"
          size="small"
          :disabled="!canGo('previous')"
          title="Previous line"
          @click="go(isRtl ? 'next' : 'previous')"
        >
          <v-icon>mdi-arrow-left-circle-outline</v-icon>
        </v-btn>
        <v-btn
          icon
          variant="text"
          size="small"
          :disabled="!canGo('next')"
          title="Next line"
          @click="go(isRtl ? 'previous' : 'next')"
        >
          <v-icon>mdi-arrow-right-circle-outline</v-icon>
        </v-btn>

        <div class="escr-section">
          <v-btn
            :variant="vkEnabled ? 'flat' : 'text'"
            :color="vkEnabled ? 'primary' : undefined"
            size="small"
            icon
            title="Virtual keyboard"
            @click="$emit('toggle-vk')"
          >
            <v-icon>mdi-keyboard-outline</v-icon>
          </v-btn>
        </div>

        <div class="escr-section">
          <v-btn
            icon
            variant="text"
            size="small"
            :disabled="zoom <= MIN_ZOOM"
            title="Zoom out"
            @click="setZoom(zoom / 1.25)"
          >
            <v-icon>mdi-magnify-minus-outline</v-icon>
          </v-btn>
          <v-btn
            icon
            variant="text"
            size="small"
            :disabled="zoom === 1"
            title="Reset zoom"
            @click="setZoom(1)"
          >
            <v-icon>mdi-magnify-scan</v-icon>
          </v-btn>
          <v-btn
            icon
            variant="text"
            size="small"
            :disabled="zoom >= MAX_ZOOM"
            title="Zoom in"
            @click="setZoom(zoom * 1.25)"
          >
            <v-icon>mdi-magnify-plus-outline</v-icon>
          </v-btn>
        </div>

        <div class="escr-section">
          <v-btn-toggle
            :model-value="line.encryption_status"
            density="compact"
            divided
            mandatory
            rounded="lg"
            class="escr-toggle"
            @update:modelValue="val => $emit('update:encryption', val)"
          >
            <v-btn value="encrypted" color="blue darken-2" title="Encrypted">
              <v-icon size="18">mdi-lock</v-icon>
            </v-btn>
            <v-btn value="mixed" color="blue darken-2" title="Mixed">
              <v-icon size="18">mdi-lock-percent-outline</v-icon>
            </v-btn>
            <v-btn value="clear_text" color="blue darken-2" title="Plain text">
              <v-icon size="18">mdi-alphabetical-variant</v-icon>
            </v-btn>
          </v-btn-toggle>

          <v-btn-toggle
            :model-value="line.status"
            density="compact"
            divided
            mandatory
            rounded="lg"
            class="escr-toggle ml-2"
            @update:modelValue="val => $emit('update:status', val)"
          >
            <v-btn
              v-for="option in statusOptions"
              :key="option.value"
              :value="option.value"
              :disabled="!allowedStatuses.includes(option.value)"
              :color="line.status === option.value ? option.color : 'grey lighten-2'"
              :title="option.label"
            >
              <v-icon
                size="18"
                :color="line.status === option.value ? 'white' : undefined"
                >{{ option.icon }}</v-icon
              >
            </v-btn>
          </v-btn-toggle>
        </div>

        <div class="escr-line-modal-right">
          <v-btn icon variant="text" size="small" title="Close" @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </div>

      <v-divider />

      <div class="escr-line-modal-body">
        <p v-if="!hasMask" class="escr-warning">
          No mask found for the line, preview unavailable
        </p>

        <div
          ref="imgContainer"
          class="escr-img-container"
          :class="{ 'escr-img-pan': zoom > 1 }"
        >
          <img
            ref="lineImg"
            :src="pageImage"
            draggable="false"
            @load="onImageLoad"
          />
          <div ref="imgOverlay" class="escr-img-overlay">
            <svg width="100%" height="100%">
              <defs>
                <mask :id="maskId">
                  <rect width="100%" height="100%" fill="white" />
                  <polygon :points="maskPoints" />
                </mask>
              </defs>
              <rect
                fill="grey"
                opacity="0.5"
                width="100%"
                height="100%"
                :mask="`url(#${maskId})`"
              />
            </svg>
          </div>
        </div>

        <div class="escr-glyph-row">
          <div class="escr-glyph-label">
            <v-icon size="14" class="mr-1">mdi-format-font</v-icon>
            Transcription preview
          </div>
          <canvas
            v-show="glyphFont"
            ref="glyphCanvas"
            class="escr-glyph-canvas"
          ></canvas>
          <span v-if="!glyphFont" class="escr-no-content">
            No glyph font yet, generate one from a keyboard
          </span>
        </div>

        <div class="escr-input-container" @keydown.capture="keepUndoKeys">
          <input
            ref="transInput"
            v-model="localText"
            class="escr-trans-input display-virtual-keyboard"
            autocomplete="off"
            :dir="isRtl ? 'rtl' : 'ltr'"
            :readonly="readonly"
            @keyup.up="go('previous')"
            @keyup.down="go('next')"
            @blur="$emit('flush')"
          />
          <small class="escr-input-note">
            <span v-if="readonly">This line is validated</span>
            <span v-else>Changes are saved as you type</span>
          </small>
        </div>

        <v-divider class="my-2" />

        <details class="escr-details">
          <summary><span>Transcription history</span></summary>

          <div v-if="historyLoading" class="escr-no-content">Loading history</div>
          <div v-else-if="historyError" class="escr-no-content">
            {{ historyError }}
          </div>
          <div v-else-if="!history.length" class="escr-no-content">
            No history to display
          </div>
          <div v-else class="escr-history">
            <div
              v-for="entry in history"
              :key="entry.id"
              class="escr-line-version"
            >
              <div class="escr-line-content" :dir="isRtl ? 'rtl' : 'ltr'">
                <span
                  v-for="(part, i) in diffParts(entry)"
                  :key="i"
                  :class="'cmp-' + part.type"
                  >{{ part.value }}</span
                >
              </div>
              <div class="escr-version-author">
                {{ entry.changed_by_username || 'unknown' }}
              </div>
              <div class="escr-version-date">
                {{ formatDate(entry.created_at) }}
              </div>
              <div class="escr-version-action">
                <v-btn
                  icon
                  variant="text"
                  size="x-small"
                  :disabled="readonly"
                  title="Load this state"
                  @click="$emit('revert', entry.old_text_content)"
                >
                  <v-icon size="18">mdi-restore</v-icon>
                </v-btn>
              </div>
            </div>
          </div>
        </details>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import api from "@/plugins/axios";
import { drawGlyphLine } from "@/plugins/glyphFont";

const H_CONTEXT = 0.3;
const MAX_DIFF_CELLS = 400000;
const MIN_ZOOM = 0.5;
const MAX_UPSCALE = 3;
const MAX_ZOOM = 4;

function patchValueSetter(element) {
  const proto = Object.getPrototypeOf(element);
  const descriptor = Object.getOwnPropertyDescriptor(proto, "value");
  if (!descriptor || !descriptor.set) return;
  let isSetting = false;
  let currentValue = element.value;
  Object.defineProperty(element, "value", {
    configurable: true,
    enumerable: true,
    get() {
      return descriptor.get.call(this);
    },
    set(newVal) {
      if (isSetting) {
        descriptor.set.call(this, newVal);
        currentValue = newVal;
        return;
      }
      if (newVal === currentValue) return;
      isSetting = true;
      descriptor.set.call(this, newVal);
      currentValue = newVal;
      this.dispatchEvent(new InputEvent("input", { bubbles: true }));
      isSetting = false;
    },
  });
}

function diffChars(oldText, newText) {
  const a = Array.from(oldText || "");
  const b = Array.from(newText || "");
  const parts = [];
  const push = (type, value) => {
    const last = parts[parts.length - 1];
    if (last && last.type === type) last.value += value;
    else parts.push({ type, value });
  };
  if (a.length * b.length > MAX_DIFF_CELLS) {
    if (a.length) push("removed", a.join(""));
    if (b.length) push("added", b.join(""));
    return parts;
  }
  const table = [];
  for (let i = 0; i <= a.length; i++) table.push(new Uint32Array(b.length + 1));
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      table[i][j] =
        a[i] === b[j]
          ? table[i + 1][j + 1] + 1
          : Math.max(table[i + 1][j], table[i][j + 1]);
    }
  }
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      push("same", a[i]);
      i += 1;
      j += 1;
    } else if (table[i + 1][j] >= table[i][j + 1]) {
      push("removed", a[i]);
      i += 1;
    } else {
      push("added", b[j]);
      j += 1;
    }
  }
  while (i < a.length) {
    push("removed", a[i]);
    i += 1;
  }
  while (j < b.length) {
    push("added", b[j]);
    j += 1;
  }
  return parts;
}

export default {
  name: "LineTranscriptionModal",
  props: {
    modelValue: { type: Boolean, default: false },
    line: { type: Object, default: null },
    lines: { type: Array, default: () => [] },
    pageImage: { type: String, default: "" },
    isRtl: { type: Boolean, default: false },
    vkEnabled: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    statusOptions: { type: Array, default: () => [] },
    allowedStatuses: { type: Array, default: () => [] },
    glyphFont: { type: Object, default: null },
    glyphShowFade: { type: Boolean, default: true },
    glyphShowMask: { type: Boolean, default: true },
    projectId: { type: [Number, String], default: null },
    documentId: { type: [Number, String], default: null },
    pageId: { type: [Number, String], default: null },
  },
  emits: [
    "update:modelValue",
    "update:text",
    "update:status",
    "update:encryption",
    "navigate",
    "toggle-vk",
    "revert",
    "flush",
  ],
  data() {
    return {
      MIN_ZOOM,
      MAX_ZOOM,
      zoom: 1,
      dragX: 0,
      dragY: 0,
      imageWidth: 0,
      imageHeight: 0,
      layout: null,
      history: [],
      historyLoading: false,
      historyError: "",
      diffCache: new Map(),
    };
  },
  computed: {
    contentStyle() {
      const moved = this.dragX || this.dragY;
      return {
        alignSelf: "flex-start",
        transform: moved
          ? `translate(${this.dragX}px, ${this.dragY}px)`
          : undefined,
      };
    },
    localText: {
      get() {
        return this.line?.text_content || "";
      },
      set(value) {
        this.$emit("update:text", value);
      },
    },
    hasMask() {
      return !!this.line?.mask?.length;
    },
    maskId() {
      return `escr-line-mask-${this.line?.id || 0}`;
    },
    maskPoints() {
      const ratio = this.layout ? this.layout.ratio : 0;
      if (!this.hasMask || !ratio) return "";
      return this.line.mask
        .map((pt) => `${Math.round(pt[0] * ratio)},${Math.round(pt[1] * ratio)}`)
        .join(" ");
    },
    lineIndex() {
      if (!this.line) return -1;
      return this.lines.findIndex((l) => l.id === this.line.id);
    },
  },
  watch: {
    modelValue(open) {
      if (open) {
        this.dragX = 0;
        this.dragY = 0;
        this.onLineChanged();
      }
    },
    line() {
      if (this.modelValue) {
        this.zoom = 1;
        this.onLineChanged();
      }
    },
    localText() {
      this.scheduleGlyphDraw();
    },
    vkEnabled() {
      this.applyVk();
    },
  },
  created() {
    this.glyphTimer = null;
    this.drag = null;
  },
  beforeUnmount() {
    if (this.glyphTimer) clearTimeout(this.glyphTimer);
    this.stopDrag();
  },
  methods: {
    close() {
      this.$emit("update:modelValue", false);
    },

    keepUndoKeys(event) {
      if (!event.ctrlKey && !event.metaKey) return;
      const key = event.key.toLowerCase();
      if (key === "z" || key === "y") event.stopPropagation();
    },

    startDrag(event) {
      if (event.button !== 0 || event.target.closest("button")) return;
      event.preventDefault();
      this.drag = {
        x: event.clientX - this.dragX,
        y: event.clientY - this.dragY,
      };
      window.addEventListener("mousemove", this.onDrag);
      window.addEventListener("mouseup", this.stopDrag);
    },

    onDrag(event) {
      if (!this.drag) return;
      this.dragX = event.clientX - this.drag.x;
      this.dragY = event.clientY - this.drag.y;
    },

    stopDrag() {
      this.drag = null;
      window.removeEventListener("mousemove", this.onDrag);
      window.removeEventListener("mouseup", this.stopDrag);
    },

    canGo(direction) {
      if (this.lineIndex < 0) return false;
      return direction === "next"
        ? this.lineIndex < this.lines.length - 1
        : this.lineIndex > 0;
    },

    go(direction) {
      if (!this.canGo(direction)) return;
      this.$emit("navigate", direction);
    },

    setZoom(value) {
      this.zoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));
      this.computeLayout();
      this.scheduleGlyphDraw();
    },

    async onLineChanged() {
      await this.$nextTick();
      this.readImageSize();
      await this.waitForContainer();
      this.computeLayout();
      this.focusInput();
      this.applyVk();
      this.fetchHistory();
      this.scheduleGlyphDraw();
    },

    readImageSize() {
      const img = this.$refs.lineImg;
      if (img && img.complete && img.naturalWidth) {
        this.imageWidth = img.naturalWidth;
        this.imageHeight = img.naturalHeight;
      }
    },

    async waitForContainer() {
      for (let i = 0; i < 40; i++) {
        const container = this.$refs.imgContainer;
        if (container && container.clientWidth > 0 && this.imageWidth) return;
        await new Promise((resolve) => requestAnimationFrame(resolve));
        this.readImageSize();
      }
    },

    onImageLoad(event) {
      this.imageWidth = event.target.naturalWidth;
      this.imageHeight = event.target.naturalHeight;
      this.computeLayout();
      this.scheduleGlyphDraw();
    },

    focusInput() {
      const input = this.$refs.transInput;
      if (input && !this.readonly) input.focus();
    },

    applyVk() {
      const input = this.$refs.transInput;
      if (!input) return;
      if (this.vkEnabled) {
        input.onfocus = null;
        if (typeof window.enableVirtualKeyboard === "function") {
          setTimeout(() => {
            window.enableVirtualKeyboard(input);
            patchValueSetter(input);
            input.focus();
          }, 50);
        }
      } else {
        input.onfocus = null;
        try {
          if (input._virtualKeyboard) input._virtualKeyboard.detach();
        } catch (err) {
          console.warn("Error detaching VK:", err);
        }
        input._virtualKeyboard = null;
        input.blur();
        if (!this.readonly) setTimeout(() => input.focus(), 200);
      }
    },

    lineAngle() {
      const mask = this.line.mask || [];
      const baseline = this.line.baseline;
      let p1;
      let p2;
      if (baseline && baseline.length > 1) {
        p1 = baseline[0];
        p2 = baseline[baseline.length - 1];
      } else {
        p1 = mask.reduce((min, pt) => (pt[0] < min[0] ? pt : min));
        p2 = mask.reduce((max, pt) => (pt[0] > max[0] ? pt : max));
      }
      return (Math.atan2(p2[1] - p1[1], p2[0] - p1[0]) * 180) / Math.PI;
    },

    rotatedBBox(angle) {
      const rad = (angle * Math.PI) / 180;
      const cx = this.imageWidth / 2;
      const cy = this.imageHeight / 2;
      const cos = Math.cos(rad);
      const sin = Math.sin(rad);
      const xs = [];
      const ys = [];
      this.line.mask.forEach(([x, y]) => {
        const dx = x - cx;
        const dy = y - cy;
        xs.push(cx + dx * cos - dy * sin);
        ys.push(cy + dx * sin + dy * cos);
      });
      const left = Math.min(...xs);
      const top = Math.min(...ys);
      return {
        left,
        top,
        width: Math.max(...xs) - left,
        height: Math.max(...ys) - top,
      };
    },

    computeLayout() {
      const container = this.$refs.imgContainer;
      const img = this.$refs.lineImg;
      const overlay = this.$refs.imgOverlay;
      if (!container || !img || !overlay) return;
      if (!this.hasMask || !this.imageWidth || !this.imageHeight) return;

      const angle = -this.lineAngle();
      const box = this.rotatedBBox(angle);
      if (!box.width || !box.height) return;

      const available = container.clientWidth || container.offsetWidth;
      // a tiny line must not be blown up until it is only blur
      let ratio = Math.min(
        MAX_UPSCALE,
        available / (box.width + 2 * box.height * H_CONTEXT)
      );
      let lineHeight = Math.max(30, Math.round(box.height * ratio));
      // keeps tall or squarish lines from filling the whole screen
      const maxPanel = Math.max(140, Math.min(260, window.innerHeight * 0.28));
      const maxLineHeight = Math.max(
        30,
        Math.round(maxPanel / (1 + 2 * H_CONTEXT))
      );
      if (lineHeight > maxLineHeight) {
        ratio = (maxLineHeight / lineHeight) * ratio;
        lineHeight = maxLineHeight;
      }

      ratio *= this.zoom;
      lineHeight = Math.round(lineHeight * this.zoom);

      const context = H_CONTEXT * lineHeight;
      const scaledWidth = this.imageWidth * ratio;
      const scaledHeight = this.imageHeight * ratio;
      const origin = `${scaledWidth / 2}px ${scaledHeight / 2}px`;
      let left = -(box.left * ratio - context);
      const top = -(box.top * ratio - context);
      if (this.isRtl && available - 2 * context > box.width * ratio) {
        left += available - 2 * context - box.width * ratio;
      }
      const transform = `translate(${left}px, ${top}px) rotate(${angle}deg)`;

      container.style.height = `${Math.round(lineHeight + 2 * context)}px`;
      img.style.width = `${scaledWidth}px`;
      img.style.transformOrigin = origin;
      img.style.transform = transform;
      overlay.style.width = `${scaledWidth}px`;
      overlay.style.height = `${scaledHeight}px`;
      overlay.style.transformOrigin = origin;
      overlay.style.transform = transform;

      this.layout = {
        ratio,
        placement: {
          ratio,
          angle,
          left,
          top,
          originX: scaledWidth / 2,
          originY: scaledHeight / 2,
        },
      };
    },

    scheduleGlyphDraw() {
      if (this.glyphTimer) clearTimeout(this.glyphTimer);
      this.glyphTimer = setTimeout(() => this.drawGlyphs(), 200);
    },

    async drawGlyphs() {
      if (!this.modelValue || !this.glyphFont || !this.line) return;
      await this.$nextTick();
      const canvas = this.$refs.glyphCanvas;
      const container = this.$refs.imgContainer;
      if (!canvas || !container) return;
      const placed = this.glyphShowFade && this.imageWidth && this.layout;
      await drawGlyphLine(canvas, {
        font: this.glyphFont,
        text: this.localText,
        isRtl: this.isRtl,
        width: Math.max(200, container.clientWidth),
        height: Math.max(40, container.clientHeight),
        image: placed ? this.$refs.lineImg : null,
        mask: this.line.mask,
        clipToMask: this.glyphShowMask,
        placement: placed ? this.layout.placement : null,
      });
    },

    async fetchHistory() {
      if (!this.line || !this.projectId) return;
      this.historyLoading = true;
      this.historyError = "";
      this.diffCache.clear();
      try {
        const { data } = await api.get(
          `/api/projects/${this.projectId}/documents/${this.documentId}/pages/${this.pageId}/lines/${this.line.id}/history/`
        );
        this.history = data;
      } catch (error) {
        console.error("Error fetching line history:", error);
        this.historyError = "Failed to load history";
      } finally {
        this.historyLoading = false;
      }
    },

    diffParts(entry) {
      if (!this.diffCache.has(entry.id)) {
        this.diffCache.set(
          entry.id,
          diffChars(entry.old_text_content, entry.new_text_content)
        );
      }
      return this.diffCache.get(entry.id);
    },

    formatDate(value) {
      if (!value) return "";
      return new Date(value).toLocaleString([], {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
};
</script>

<style scoped>
.escr-line-modal {
  border-radius: 6px;
}

.escr-line-modal-header {
  display: flex;
  flex-flow: row wrap;
  align-items: center;
  row-gap: 4px;
  padding: 8px 16px;
  cursor: move;
  user-select: none;
}

.escr-line-modal-header h2 {
  font-size: 1.05rem;
  font-weight: 600;
  margin: 0 1.5rem 0 0;
}

.escr-section {
  display: flex;
  align-items: center;
  margin-left: 12px;
  padding-left: 12px;
  border-left: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.escr-toggle {
  height: 32px;
  cursor: default;
}

.escr-line-modal-right {
  flex: 1 0 auto;
  display: flex;
  justify-content: flex-end;
}

.escr-line-modal-body {
  padding: 12px 16px 16px;
}

.escr-warning {
  font-size: 0.85rem;
  color: rgb(var(--v-theme-warning));
  margin-bottom: 8px;
}

.escr-img-container {
  position: relative;
  overflow: hidden;
  background-color: #fff;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.escr-img-pan {
  overflow-x: auto;
  overflow-y: hidden;
}

.escr-img-container img {
  position: absolute;
  top: 0;
  left: 0;
  max-width: none;
  user-select: none;
}

.escr-img-overlay {
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
}

.escr-glyph-row {
  margin-top: 10px;
}

.escr-glyph-label {
  display: flex;
  align-items: center;
  font-size: 0.75rem;
  color: rgba(var(--v-theme-on-surface), 0.6);
  margin-bottom: 4px;
}

.escr-glyph-canvas {
  display: block;
  width: 100%;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.escr-input-container {
  position: relative;
  margin-top: 10px;
}

.escr-trans-input {
  display: block;
  width: 100%;
  font-size: 16px;
  line-height: 1.5;
  padding: 8px 10px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 4px;
  background-color: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-on-surface));
  outline: none;
}

.escr-trans-input:focus {
  border-color: rgb(var(--v-theme-primary));
}

.escr-input-note {
  display: block;
  margin-top: 4px;
  font-size: 0.72rem;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

.escr-details summary {
  display: flex;
  align-items: center;
  min-height: 32px;
  cursor: pointer;
  list-style: none;
}

.escr-details summary::-webkit-details-marker {
  display: none;
}

.escr-details summary::before {
  content: "\25B6";
  font-size: 11px;
}

.escr-details[open] > summary::before {
  content: "\25BC";
}

.escr-details summary > span {
  font-size: 14px;
  font-weight: 600;
  margin-left: 12px;
}

.escr-no-content {
  display: block;
  font-size: 14px;
  margin: 12px;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

.escr-history {
  display: table;
  width: 100%;
  margin-top: 8px;
}

.escr-line-version {
  display: table-row;
}

.escr-line-version > div {
  display: table-cell;
  vertical-align: middle;
  padding: 4px 8px 4px 0;
  font-size: 80%;
}

.escr-line-content {
  width: 100%;
  font-size: 100%;
  word-break: break-word;
}

.escr-version-author,
.escr-version-date {
  white-space: nowrap;
  color: rgba(var(--v-theme-on-surface), 0.7);
}

.escr-version-author {
  max-width: 12rem;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cmp-added {
  background-color: rgba(76, 175, 80, 0.3);
}

.cmp-removed {
  background-color: rgba(244, 67, 54, 0.3);
  text-decoration: line-through;
}
</style>
